import React, { useEffect, useMemo, useRef, useState } from 'react';

const clamp01 = (n) => Math.max(0, Math.min(1, n));

const AnimatedText = ({
  text,
  speed = 22,
  chunkSize = 2,
  pauseMultiplier = 4,
  startDelay = 120,
  thinkingDelay = 220,
  showThinking = true,
  thinkingGlyph = '…',
  jitter = 0.18,
  allowSkip = false,
  onDone,
}) => {
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState('idle'); // 'idle' | 'thinking' | 'typing' | 'done'
  const timeoutRef = useRef(null);
  const doneRef = useRef(false);

  const reducedMotion = useMemo(() => false, []);

  const clearTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  useEffect(() => {
    clearTimer();
    doneRef.current = false;

    if (!text) {
      setIndex(0);
      setPhase('idle');
      return;
    }

    if (reducedMotion) {
      setIndex(text.length);
      setPhase('done');
      doneRef.current = true;
      onDone?.();
      return;
    }

    setIndex(0);
    setPhase(showThinking ? 'thinking' : 'typing');

    const totalDelay = startDelay + (showThinking ? thinkingDelay : 0);

    timeoutRef.current = setTimeout(() => {
      setPhase('typing');
    }, totalDelay);

    return clearTimer;
  }, [text, showThinking, startDelay, thinkingDelay, reducedMotion, onDone]);

  useEffect(() => {
    if (!text) return;
    if (phase !== 'typing') return;
    if (index >= text.length) return;

    const currentChar = text[index];
    let delay = speed;

    if (currentChar === '\n') delay = speed * (pauseMultiplier + 1.2);
    else if (/[.,!?]/.test(currentChar)) delay = speed * pauseMultiplier;
    else if (/[:;)]/.test(currentChar)) delay = speed * (pauseMultiplier * 0.75);
    else if (currentChar === ' ') delay = Math.max(10, speed * 0.62);

    const jitterFactor = 1 + (Math.random() * 2 - 1) * clamp01(jitter);
    const finalDelay = Math.max(10, Math.round(delay * jitterFactor));

    timeoutRef.current = setTimeout(() => {
      setIndex((prev) => Math.min(text.length, prev + Math.max(1, chunkSize)));
    }, finalDelay);

    return clearTimer;
  }, [text, index, speed, chunkSize, pauseMultiplier, jitter, phase]);

  useEffect(() => {
    if (!text) return;
    if (phase === 'done') return;

    if (index >= text.length && !doneRef.current) {
      doneRef.current = true;
      setPhase('done');
      onDone?.();
    }
  }, [index, text, phase, onDone]);

  const safeIndex = text ? Math.min(index, text.length) : 0;
  const displayed = text ? text.slice(0, safeIndex) : '';
  const isDone = !!text && displayed.length >= text.length;

  const handleSkip = () => {
    if (!allowSkip || !text) return;
    if (isDone) return;
    clearTimer();
    setIndex(text.length);
    setPhase('done');
    if (!doneRef.current) {
      doneRef.current = true;
      onDone?.();
    }
  };

  return (
    <span
      className="animated-text"
      onClick={handleSkip}
      role={allowSkip ? 'button' : undefined}
      aria-label={allowSkip ? 'Skip typing animation' : undefined}
      tabIndex={allowSkip ? 0 : undefined}
      onKeyDown={(e) => {
        if (!allowSkip) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleSkip();
        }
      }}
      style={{ cursor: allowSkip ? 'pointer' : 'default' }}
    >
      {phase === 'thinking' && <span className="animated-text-thinking">{thinkingGlyph}</span>}
      {displayed}
      {!isDone && phase === 'typing' && <span className="animated-text-cursor" aria-hidden="true" />}
    </span>
  );
};

export default AnimatedText;
